import { Component } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { includes, get, set, cloneDeep, chain, isEmpty, map, size, noop } from "lodash"
import pluralize from "pluralize"
import { Button, Tab, Tabs, Card, CardHeader } from "@mui/material"
import { SEGMENT } from "helpers/shapes"
import { formatLongDate } from "helpers/formatters/dates"
import { getSegmentLabel } from "helpers/segments"
import SideSheet from "components/material/side-sheet"
import EditSwap from "connected-components/edit-swap"
import ExcelDownload from "components/excel-download"
import { INSTRUMENT_SIZE_SHAPE } from "./helpers"
import Summary from "./components/summary"
import Rules from "./components/rules"
import Positions from "./components/positions"
import "./style.scss"

function updateFilters(filters, removedSecurities) {
  const updatedFilters = cloneDeep(filters)
  const notIn = get(updatedFilters, "securities.notIn", [])
  const updatedNotIn = chain(removedSecurities)
    .map(elem => ({ ticker: elem.ticker, mic: elem.mic }))
    .filter(elem => elem)
    .concat(notIn)
    .value()

  set(updatedFilters, "securities.notIn", updatedNotIn)
  return updatedFilters
}

class SmartTradePanel extends Component {
  static propTypes = {
    account: PropTypes.string.isRequired,
    modelId: PropTypes.string.isRequired,
    fileName: PropTypes.string.isRequired,
    csvRows: PropTypes.array.isRequired,
    selectedTab: PropTypes.string.isRequired,
    setSelectedPanelTab: PropTypes.func.isRequired,
    basketSummaryData: PropTypes.array,
    smartTradeSummaryData: PropTypes.array.isRequired,
    groups: PropTypes.array.isRequired,
    selectedGroup: PropTypes.string.isRequired,
    setSelectedGroup: PropTypes.func.isRequired,
    selectedDate: PropTypes.string.isRequired,
    setOptimizationRules: PropTypes.func.isRequired,
    status: PropTypes.array.isRequired,
    setActiveBasketRule: PropTypes.func.isRequired,
    computedOptimizationRules: PropTypes.object.isRequired,
    tabsEnabled: PropTypes.array.isRequired,
    basketRules: PropTypes.array.isRequired,
    rulesApplyButtonEnabled: PropTypes.bool.isRequired,
    positionsApplyEnabled: PropTypes.bool.isRequired,
    basketSecurities: PropTypes.array.isRequired,
    refInstrumentPositions: PropTypes.array.isRequired,
    loadSecurityFinderFilters: PropTypes.func.isRequired,
    resetSecurityFinderFilters: PropTypes.func.isRequired,
    removeSecurities: PropTypes.func.isRequired,
    applyRemovedSecurities: PropTypes.func.isRequired,
    removedSecurities: PropTypes.array.isRequired,
    filters: PropTypes.object,
    resetSecurities: PropTypes.func.isRequired,
    openDialog: PropTypes.func.isRequired,
    resetRules: PropTypes.func.isRequired,
    resetActiveRules: PropTypes.func.isRequired,
    isReadOnly: PropTypes.bool.isRequired,
    panelOpen: PropTypes.bool,
    toggleOpenPanel: PropTypes.func,
    createSwap: PropTypes.func.isRequired,
    smartTradeRulesSearchResults: PropTypes.array,
    smartTradeRulesSearch: PropTypes.func.isRequired,
    smartTradeRulesSearchValue: PropTypes.string,
    resetSmartTradeRules: PropTypes.func.isRequired,
    setSmartTradeRule: PropTypes.func.isRequired,
    removeSmartTradeRule: PropTypes.func.isRequired,
    smartTradeRules: PropTypes.array.isRequired,
    currentSmartTradeValues: PropTypes.object,
    securitiesCount: PropTypes.number,
    adjustedSecuritiesCount: PropTypes.number,
    adjustSecurityCount: PropTypes.func,
    dispatch: PropTypes.func.isRequired,
    selectedExperimentPositionSet: PropTypes.object,
    experiment: PropTypes.object,
    segment: SEGMENT,
    instrumentPortfolio: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    instrumentSize: INSTRUMENT_SIZE_SHAPE.isRequired,
    openSecurityProfile2: PropTypes.func.isRequired,
  }

  onTabChanged = (_, value) => {
    const { setSelectedPanelTab } = this.props
    setSelectedPanelTab(value)
  }

  onApplyRules = () => {
    const { setOptimizationRules, computedOptimizationRules, resetSecurities } = this.props
    setOptimizationRules(computedOptimizationRules)
    resetSecurities()
  }

  onApplyPositions = () => {
    const { filters, applyRemovedSecurities, removedSecurities, loadSecurityFinderFilters } = this.props
    const updatedFilters = updateFilters(filters, removedSecurities)

    loadSecurityFinderFilters(updatedFilters)
    applyRemovedSecurities()
  }

  removeSecurities = (securities, callback = noop) => {
    const { openDialog, filters, removeSecurities, applyRemovedSecurities, loadSecurityFinderFilters, securitiesCount, adjustedSecuritiesCount, adjustSecurityCount } = this.props
    if (isEmpty(securities)) { return null }

    openDialog({
      title: "Remove securities",
      contentText: `${size(securities)} ${pluralize("security", size(securities))} will been removed from this smart trade's universe. These can be adjusted in Select Universe by clicking on the "Securities not in [...]" list.`,
      buttons: [{
        text: "Remove",
        type: "primary",
        action: () => {
          removeSecurities(securities)
          const filterFormattedRemovedSecurities = map(securities, (security) => ({ ...security.id }))
          loadSecurityFinderFilters(updateFilters(filters, filterFormattedRemovedSecurities))
          adjustSecurityCount((adjustedSecuritiesCount || securitiesCount) - securities.length)
          applyRemovedSecurities()
          callback()
        },
      }],
    })
  }

  onResetRules = () => {
    const { openDialog, resetActiveRules, resetRules, resetSmartTradeRules } = this.props
    openDialog({
      title: "Reset",
      contentText: "This will reset all rules previously set for the smart trade.",
      buttons: [{
        text: "Confirm",
        type: "primary",
        action: () => {
          resetActiveRules()
          resetRules()
          resetSmartTradeRules()
        },
      }],
    })
  }

  onCreateBasketTicker = () => {
    const { openDialog, createSwap } = this.props
    openDialog({
      title: "Create Basket",
      content: <EditSwap saveSwap={createSwap} />,
    })
  }

  render() {
    const {
      fileName,
      csvRows,
      selectedTab,
      basketSummaryData,
      smartTradeSummaryData,
      groups,
      selectedGroup,
      setSelectedGroup,
      selectedDate,
      basketRules,
      setActiveBasketRule,
      status,
      tabsEnabled,
      rulesApplyButtonEnabled,
      positionsApplyEnabled,
      isReadOnly,
      panelOpen,
      toggleOpenPanel,
      smartTradeRulesSearchResults,
      smartTradeRulesSearch,
      smartTradeRulesSearchValue,
      resetSmartTradeRules,
      setSmartTradeRule,
      removeSmartTradeRule,
      smartTradeRules,
      currentSmartTradeValues,
      dispatch,
      segment,
      experiment,
      selectedExperimentPositionSet,
      instrumentPortfolio,
      instrumentSize,
      refInstrumentPositions,
      basketSecurities,
      openSecurityProfile2,
    } = this.props

    const smartLabel = isEmpty(instrumentPortfolio) ? "trade" : "portfolio"

    const innerComponents = <>
      <Tabs
        value={selectedTab}
        onChange={this.onTabChanged}
        className="smart-trade-tabs"
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab value="summary" disabled={!includes(tabsEnabled, "summary")} label="Summary" />
        <Tab value="positions" disabled={!includes(tabsEnabled, "positions")} label={!isEmpty(instrumentPortfolio) ? "Basket" : "Positions"} />
        {isEmpty(segment) && <Tab value="rules" label="Rules" />}
      </Tabs>
      <div className="active-tab">
        {selectedTab === "summary" && <Summary
          basketSummaryData={basketSummaryData}
          smartTradeSummaryData={smartTradeSummaryData}
          groups={groups}
          selectedGroup={selectedGroup}
          setSelectedGroup={setSelectedGroup}
          status={status}
          dispatch={dispatch}
          smartLabel={smartLabel}
        />}
        {selectedTab === "positions" && <Positions
          securities={basketSecurities}
          refSecurities={refInstrumentPositions}
          removeSecurities={this.removeSecurities}
          isSegment={!isEmpty(segment)}
          status={status}
          onApply={this.onApplyPositions}
          applyButtonEnabled={positionsApplyEnabled}
          isReadOnly={isReadOnly}
          dispatch={dispatch}
          instrumentPortfolio={instrumentPortfolio}
          instrumentSize={instrumentSize}
          openSecurityProfile2={openSecurityProfile2}
        />}
        {selectedTab === "rules" && <Rules
          basketRules={basketRules}
          applyButtonEnabled={rulesApplyButtonEnabled}
          setActiveBasketRule={setActiveBasketRule}
          onApplyRules={this.onApplyRules}
          hasError={!includes(tabsEnabled, "summary")}
          resetRules={this.onResetRules}
          isReadOnly={isReadOnly}
          smartTradeRulesSearchResults={smartTradeRulesSearchResults}
          smartTradeRulesSearch={smartTradeRulesSearch}
          smartTradeRulesSearchValue={smartTradeRulesSearchValue}
          resetSmartTradeRules={resetSmartTradeRules}
          setSmartTradeRule={setSmartTradeRule}
          removeSmartTradeRule={removeSmartTradeRule}
          smartTradeRules={smartTradeRules}
          currentSmartTradeValues={currentSmartTradeValues}
          selectedDate={selectedDate}
          smartLabel={smartLabel}
          dispatch={dispatch}
        />}
      </div>
    </>

    const title = segment ? getSegmentLabel(segment, experiment) : get(experiment, "label", `Smart ${smartLabel}`)
    const subheader = segment ? `Viewing segment on ${formatLongDate(selectedDate)}` : formatLongDate(selectedDate)
    const tooltip = !isEmpty(instrumentPortfolio) ? "Download Basket Positions" : "Download Smart Trade Positions"

    return <div className={classNames("modules_smart-trade-panel", { "read-only": isReadOnly })}>
      {toggleOpenPanel
        ? <SideSheet open={panelOpen} title={title} subheader={subheader} onClose={() => toggleOpenPanel()}>
          <div className={classNames("sheet-smart-trade-panel-wrapper", { "segment-view": segment })}>
            <ExcelDownload data={csvRows} fileName={fileName} title={tooltip} />
            <div className="inner-components">
              {innerComponents}
            </div>
            {selectedExperimentPositionSet && !segment
              && <div className="save-buttons-bar">
                <Button variant="contained" color="primary" onClick={this.onCreateBasketTicker}>Create Basket Ticker</Button>
              </div>}
          </div>
        </SideSheet>
        : <Card square className="smart-trade-panel-wrapper">
          <CardHeader
            title={title}
            subheader={formatLongDate(selectedDate)}
            action={<ExcelDownload data={csvRows} fileName={fileName} title={tooltip} />}
          />
          {innerComponents}
        </Card>}
    </div>
  }
}

export default SmartTradePanel
